import { useState, useEffect, useRef } from "react";
import { collection, onSnapshot, query, orderBy, limit } from "firebase/firestore";
import Icon from "../ui/Icon";
import MiniSpinner from "../ui/MiniSpinner";
import { BRAND, THEMES } from "../../config/constants";
import { db, auth } from "../../firebase";
import { btnP, btnS } from "../../utils/styles";

const FUNCS = "https://us-central1-netsave-crm.cloudfunctions.net";

export default function SocialPage({ theme }) {
  const t = THEMES[theme];
  const [tab, setTab] = useState("compose");
  const [message, setMessage] = useState("");
  const [link, setLink] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [postResult, setPostResult] = useState("");
  const [fbPosts, setFbPosts] = useState([]);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [crmPosts, setCrmPosts] = useState([]);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const fileRef = useRef(null);

  const inp = { background: t.inputBg || t.bg, border: "1px solid " + t.border, borderRadius: 8, padding: "9px 12px", color: t.text, fontSize: 13, outline: "none", fontFamily: "'Outfit',sans-serif", width: "100%", boxSizing: "border-box" };

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "social_posts"), orderBy("postedAt", "desc"), limit(50)),
      snap => setCrmPosts(snap.docs.map(d => ({ id: d.id, ...d.data() }))), () => {}
    );
    return unsub;
  }, []);

  // Upload image to Firebase Storage via cloud function
  const handleImageFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    if (file.size > 5 * 1024 * 1024) { setPostResult("Image too large (max 5MB)"); return; }
    setImageFile(file);
    setUploading(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(FUNCS + "/uploadSocialImage", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ imageBase64: base64, fileName: file.name, contentType: file.type }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Upload failed");
      setImageUrl(data.url);
    } catch (e) { setPostResult("Image upload error: " + e.message); setImageFile(null); }
    setUploading(false);
  };

  const handlePost = async () => {
    if (!message.trim() && !imageUrl) return;
    setPosting(true); setPostResult("");
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(FUNCS + "/postToFacebook", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ message: message.trim(), link: link.trim() || null, imageUrl: imageUrl.trim() || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Post failed");
      setPostResult("✓ Posted to Facebook!");
      setMessage(""); setLink(""); setImageUrl(""); setImageFile(null);
      setTimeout(() => setPostResult(""), 5000);
    } catch (e) { setPostResult("Error: " + e.message); }
    setPosting(false);
  };

  const loadFeed = async () => {
    setLoadingFeed(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(FUNCS + "/getFacebookPosts", { headers: { Authorization: "Bearer " + token } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setFbPosts(data.posts || []);
    } catch (e) { setFbPosts([]); }
    setLoadingFeed(false);
  };

  // AI generate — preset or custom
  const aiGenerate = async (type) => {
    setAiGenerating(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const body = type === "custom"
        ? { customPrompt: customPrompt.trim() }
        : { type };
      const res = await fetch(FUNCS + "/generateSocialPost", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      if (data.content) setMessage(data.content);
    } catch (e) { setPostResult("AI error: " + e.message); }
    setAiGenerating(false);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: t.text, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 20 }}>📣</span> Social
          </div>
          <div style={{ fontSize: 13, color: t.textMuted }}>Post to Facebook & manage your social presence</div>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {[["compose", "Compose"], ["feed", "Page Feed"], ["history", "Post History"]].map(([id, label]) => (
            <button key={id} onClick={() => { setTab(id); if (id === "feed" && fbPosts.length === 0) loadFeed(); }}
              style={{ padding: "6px 14px", borderRadius: 20, border: "1px solid " + (tab === id ? BRAND.blue + "50" : t.border), background: tab === id ? BRAND.blue + "12" : "transparent", color: tab === id ? BRAND.blue : t.textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'Outfit',sans-serif" }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ═══ COMPOSE ═══ */}
      {tab === "compose" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 16 }}>
          <div>
            <div style={{ background: t.surface, border: "1px solid " + t.border, borderRadius: 12, padding: "18px 20px" }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                <span style={{ fontSize: 12, fontWeight: 700, padding: "4px 12px", borderRadius: 20, background: "#1877F215", color: "#1877F2", border: "1px solid #1877F230" }}>📘 Facebook</span>
                <span style={{ fontSize: 12, fontWeight: 700, padding: "4px 12px", borderRadius: 20, background: t.bg, color: t.textMuted, border: "1px solid " + t.border, opacity: 0.5 }}>📸 Instagram (coming soon)</span>
              </div>

              <textarea value={message} onChange={e => setMessage(e.target.value)}
                placeholder="What would you like to share?"
                style={{ ...inp, minHeight: 140, resize: "vertical", fontSize: 14, lineHeight: 1.6 }} />
              <div style={{ fontSize: 11, color: message.length > 500 ? "#f59e0b" : t.textMuted, marginTop: 4, marginBottom: 12 }}>{message.length} characters</div>

              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Link (optional)</div>
                <input value={link} onChange={e => setLink(e.target.value)} placeholder="https://netsavetax.com/blog/..." style={inp} />
              </div>

              {/* Image: upload OR URL */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>Image (optional)</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <button onClick={() => fileRef.current?.click()} disabled={uploading}
                    style={{ ...btnS(t), padding: "7px 14px", fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
                    {uploading ? <><MiniSpinner size={12} color={BRAND.blue} /> Uploading...</> : "📤 Upload Image"}
                  </button>
                  <span style={{ fontSize: 12, color: t.textMuted }}>or</span>
                  <input value={imageUrl} onChange={e => { setImageUrl(e.target.value); setImageFile(null); }}
                    placeholder="Paste image URL" style={{ ...inp, flex: 1 }} />
                </div>
                <input ref={fileRef} type="file" accept="image/*" onChange={e => handleImageFile(e.target.files?.[0])} style={{ display: "none" }} />
                {(imageUrl || imageFile) && (
                  <div style={{ marginTop: 8, position: "relative", display: "inline-block" }}>
                    <img src={imageUrl || URL.createObjectURL(imageFile)} alt="preview" style={{ maxHeight: 120, borderRadius: 8, border: "1px solid " + t.border }} onError={e => e.target.style.display = "none"} />
                    <button onClick={() => { setImageUrl(""); setImageFile(null); }}
                      style={{ position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: "50%", background: "#f87171", color: "#fff", border: "none", cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
                  </div>
                )}
              </div>

              {postResult && (
                <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 12,
                  background: postResult.startsWith("✓") ? BRAND.green + "12" : "#f8717112",
                  border: "1px solid " + (postResult.startsWith("✓") ? BRAND.green + "30" : "#f8717130"),
                  color: postResult.startsWith("✓") ? BRAND.green : "#f87171", fontSize: 13, fontWeight: 600 }}>
                  {postResult}
                </div>
              )}

              <button onClick={handlePost} disabled={posting || (!message.trim() && !imageUrl)}
                style={{ ...btnP, width: "100%", justifyContent: "center", padding: "12px", fontSize: 14,
                  background: "linear-gradient(135deg, #1877F2, #0d5bbd)",
                  opacity: (!message.trim() && !imageUrl) ? 0.5 : 1 }}>
                {posting ? <><MiniSpinner size={14} color="#fff" /> Posting...</> : "📘 Post to Facebook"}
              </button>
            </div>
          </div>

          {/* Right: AI + Preview */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ background: t.surface, border: "1px solid " + t.border, borderRadius: 12, padding: "16px 18px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#7F77DD", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 14 }}>✦</span> AI Generate Post
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {[
                  { id: "tip", label: "Tax tip", icon: "💡" },
                  { id: "seasonal", label: "Seasonal topic", icon: "📅" },
                  { id: "promo", label: "Service promo", icon: "🏢" },
                  { id: "nra", label: "NRA tax tip", icon: "🌎" },
                ].map(item => (
                  <button key={item.id} onClick={() => aiGenerate(item.id)} disabled={aiGenerating}
                    style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid " + t.border, background: "transparent", cursor: aiGenerating ? "not-allowed" : "pointer", color: t.text, fontSize: 12.5, fontFamily: "'Outfit',sans-serif", textAlign: "left", display: "flex", alignItems: "center", gap: 8 }}>
                    <span>{item.icon}</span> {item.label}
                  </button>
                ))}

                {/* Custom prompt */}
                <div style={{ borderTop: "1px solid " + t.border, paddingTop: 8, marginTop: 4 }}>
                  <button onClick={() => setShowCustom(!showCustom)}
                    style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid " + (showCustom ? "#7F77DD40" : t.border), background: showCustom ? "#7F77DD08" : "transparent", cursor: "pointer", color: showCustom ? "#7F77DD" : t.text, fontSize: 12.5, fontFamily: "'Outfit',sans-serif", textAlign: "left", display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                    <span>✏️</span> Custom prompt {showCustom ? "▾" : "▸"}
                  </button>
                  {showCustom && (
                    <div style={{ marginTop: 8 }}>
                      <textarea value={customPrompt} onChange={e => setCustomPrompt(e.target.value)}
                        placeholder="e.g. Write a 100-word post about estimated tax payments for freelancers, include a CTA to book consultation"
                        style={{ ...inp, minHeight: 80, resize: "vertical", fontSize: 12 }} />
                      <button onClick={() => aiGenerate("custom")} disabled={aiGenerating || !customPrompt.trim()}
                        style={{ ...btnP, width: "100%", justifyContent: "center", padding: "8px", fontSize: 12, marginTop: 6, background: "linear-gradient(135deg,#7F77DD,#534AB7)", opacity: !customPrompt.trim() ? 0.5 : 1 }}>
                        {aiGenerating ? <><MiniSpinner size={12} color="#fff" /> Generating...</> : "✦ Generate"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {aiGenerating && <div style={{ marginTop: 8, fontSize: 11, color: "#7F77DD", textAlign: "center" }}><MiniSpinner size={12} color="#7F77DD" /> Generating...</div>}
            </div>

            {/* Preview */}
            {message && (
              <div style={{ background: t.surface, border: "1px solid " + t.border, borderRadius: 12, padding: "16px 18px" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10 }}>Preview</div>
                <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #ddd", padding: "12px 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#1877F2", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14, fontWeight: 700 }}>N</div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#1c1e21" }}>NetSave Tax & Accounting</div>
                      <div style={{ fontSize: 11, color: "#65676b" }}>Just now</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 13, color: "#1c1e21", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{message}</div>
                  {imageUrl && <img src={imageUrl} alt="" style={{ marginTop: 8, width: "100%", borderRadius: 6 }} onError={e => e.target.style.display = "none"} />}
                  {link && <div style={{ marginTop: 8, fontSize: 12, color: "#1877F2", wordBreak: "break-all" }}>{link}</div>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ FEED ═══ */}
      {tab === "feed" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.text }}>Facebook Page Feed</div>
            <button onClick={loadFeed} disabled={loadingFeed} style={{ ...btnS(t), padding: "6px 12px", fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
              {loadingFeed ? <MiniSpinner size={12} color={BRAND.blue} /> : <Icon name="refresh" size={12} color={BRAND.blue} />} Refresh
            </button>
          </div>
          {loadingFeed && fbPosts.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: t.textMuted }}><MiniSpinner size={20} color={BRAND.blue} /></div>
          ) : fbPosts.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: t.textMuted, background: t.surface, border: "1px solid " + t.border, borderRadius: 12 }}>No posts found. Click Refresh to load.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {fbPosts.map(post => (
                <div key={post.id} style={{ background: t.surface, border: "1px solid " + t.border, borderRadius: 12, padding: "16px 20px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#1877F2", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14, fontWeight: 700 }}>N</div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>NetSave Tax & Accounting</div>
                        <div style={{ fontSize: 11, color: t.textMuted }}>{post.created_time ? new Date(post.created_time).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : ""}</div>
                      </div>
                    </div>
                    {post.permalink_url && <a href={post.permalink_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: BRAND.blue, textDecoration: "none" }}>View on FB ↗</a>}
                  </div>
                  {post.message && <div style={{ fontSize: 13.5, color: t.text, lineHeight: 1.6, marginBottom: 8, whiteSpace: "pre-wrap" }}>{post.message}</div>}
                  {post.full_picture && <img src={post.full_picture} alt="" style={{ maxHeight: 200, borderRadius: 8, marginBottom: 8 }} />}
                  <div style={{ display: "flex", gap: 16, fontSize: 12, color: t.textMuted, paddingTop: 8, borderTop: "1px solid " + t.border }}>
                    <span>👍 {post.likes?.summary?.total_count || 0}</span>
                    <span>💬 {post.comments?.summary?.total_count || 0}</span>
                    <span>🔄 {post.shares?.count || 0}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ HISTORY ═══ */}
      {tab === "history" && (
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 14 }}>Posts from CRM ({crmPosts.length})</div>
          {crmPosts.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: t.textMuted, background: t.surface, border: "1px solid " + t.border, borderRadius: 12 }}>No posts yet.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {crmPosts.map(p => (
                <div key={p.id} style={{ background: t.surface, border: "1px solid " + t.border, borderRadius: 10, padding: "14px 18px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: "#1877F215", color: "#1877F2" }}>{p.platform === "instagram" ? "Instagram" : "Facebook"}</span>
                      <span style={{ fontSize: 11, color: t.textMuted }}>{p.postedBy}</span>
                    </div>
                    <span style={{ fontSize: 11, color: t.textMuted }}>{p.postedAt?.seconds ? new Date(p.postedAt.seconds * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}</span>
                  </div>
                  <div style={{ fontSize: 13, color: t.text, lineHeight: 1.5 }}>{p.message?.slice(0, 200)}{p.message?.length > 200 ? "..." : ""}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
